// @systeme: interface
// L'écran Aujourd'hui : ce qui est dû aujourd'hui et demain. Rien d'autre.
// Un gros bouton pour noter. Le silence (« Rien d'urgent aujourd'hui ») est un écran
// soigné comme les autres.
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Fab from '@mui/material/Fab';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { BoiteDeReception, ModeCapture, VueAujourdhui } from '@foyer/shared';
import { Link as LienRouteur } from 'react-router';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { Mic, Pencil, Wind } from 'lucide-react';
import { FeuilleCapture } from '../composants/FeuilleCapture';
import { fileAttente } from '../lib/file-attente';
import { fonctionActive } from '../lib/fonctionnalites';
import { couleurs } from '../theme/theme';
import { useActionsElements } from '../composants/ActionsElements';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { formaterDateComplete, formaterJourLong, jourDe } from '../lib/dates';
import { textes } from '../lib/textes';
import { useSessionConnectee } from '../session/SessionContext';
import { tailles } from '../theme/theme';

/** « jeudi 10 septembre » → « Jeudi 10 septembre » (en français, seul le premier mot prend la majuscule). */
export function premiereMajuscule(texte: string): string {
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

export function Aujourdhui() {
  const session = useSessionConnectee();
  const { direErreur } = useMessages();
  const [vue, setVue] = useState<VueAujourdhui | undefined>();
  const [voirUnJour, setVoirUnJour] = useState(false);
  const [voirFaites, setVoirFaites] = useState(false);
  const [boite, setBoite] = useState<BoiteDeReception | undefined>();
  const [capture, setCapture] = useState<ModeCapture | null>(null);
  const [enAttenteHorsLigne, setEnAttenteHorsLigne] = useState(0);

  const chargerBoite = useCallback(() => {
    api.captures
      .boite()
      .then(setBoite)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    chargerBoite();
    let file: ReturnType<typeof fileAttente> | undefined;
    try {
      file = fileAttente();
    } catch {
      return;
    }
    const desabonner = file.abonner((etat) => setEnAttenteHorsLigne(etat.enAttente));
    void file.envoyerTout().then((envoyees) => envoyees.length > 0 && chargerBoite());
    return desabonner;
  }, [chargerBoite]);

  // Tant qu'une capture est en cours, on regarde régulièrement si les fiches sont prêtes.
  useEffect(() => {
    if (!boite || boite.nombreEnCours === 0) return;
    const minuterie = setInterval(chargerBoite, 4000);
    return () => clearInterval(minuterie);
  }, [boite, chargerBoite]);

  const charger = useCallback(async () => {
    try {
      setVue(await api.elements.aujourdhui());
    } catch (e) {
      direErreur(e);
    }
  }, [direErreur]);

  useEffect(() => {
    api.elements.aujourdhui().then(setVue).catch(direErreur);
  }, [direErreur]);

  const { ligne, feuilles, ouvrirCreation } = useActionsElements({
    jourAujourdhui: vue?.jour ?? '',
    jourDemain: vue?.jourDemain ?? '',
    recharger: charger,
    ecran: '/',
  });

  if (!vue) return <Chargement />;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Bonjour {session.utilisateur.prenom}
      </Typography>
      <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
        {premiereMajuscule(formaterJourLong(vue.jour))}
      </Typography>

      {session.utilisateur.suppressionPrevueLe && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, bgcolor: couleurs.terreCuiteClair, borderColor: 'transparent' }}
        >
          <Typography>
            Ton compte sera supprimé le{' '}
            {formaterDateComplete(jourDe(session.utilisateur.suppressionPrevueLe))}.
          </Typography>
          <Typography
            component={LienRouteur}
            to="/moi"
            sx={{ display: 'inline-block', mt: 0.5, color: 'text.primary' }}
          >
            Changer d’avis →
          </Typography>
        </Paper>
      )}
      {session.espace.modeAllege && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, bgcolor: couleurs.saugeClair, borderColor: 'transparent' }}
        >
          <Typography>{textes.assistantEnPause}</Typography>
          {session.espace.role === 'titulaire' && (
            <Typography
              component={LienRouteur}
              to="/moi"
              sx={{ display: 'inline-block', mt: 0.5, color: 'text.primary' }}
            >
              Voir les formules →
            </Typography>
          )}
        </Paper>
      )}
      {enAttenteHorsLigne > 0 && (
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          {enAttenteHorsLigne === 1
            ? 'Un message n’est pas parti, je réessaie dès que le réseau revient.'
            : `${enAttenteHorsLigne} messages ne sont pas partis, je réessaie dès que le réseau revient.`}
        </Typography>
      )}
      {boite && boite.nombreEnCours > 0 && boite.nombreFiches === 0 && (
        <Typography color="text.secondary" sx={{ mb: 1 }} aria-live="polite">
          J’y travaille…
        </Typography>
      )}
      {boite && (boite.nombreFiches > 0 || boite.captures.some((c) => c.statut === 'erreur')) && (
        <Paper
          component={LienRouteur}
          to="/boite"
          variant="outlined"
          sx={{
            display: 'block',
            p: 1.5,
            mb: 2,
            textDecoration: 'none',
            bgcolor: couleurs.terreCuiteClair,
            borderColor: 'transparent',
            color: 'text.primary',
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            {boite.nombreFiches === 0
              ? 'Un message n’a pas pu être traité'
              : boite.nombreFiches === 1
                ? 'Une fiche à valider'
                : `${boite.nombreFiches} fiches à valider`}{' '}
            →
          </Typography>
        </Paper>
      )}

      {vue.aujourdhui.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="h2" component="p" color="primary.dark">
            {textes.rienDUrgent}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {textes.toutEstGarde}
          </Typography>
        </Box>
      ) : (
        <List disablePadding aria-label="Aujourd’hui">
          {vue.aujourdhui.map(ligne)}
        </List>
      )}

      {vue.demain.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 1 }}>
            Demain
          </Typography>
          <List disablePadding aria-label="Demain">
            {vue.demain.map(ligne)}
          </List>
        </Box>
      )}

      {vue.sansDate.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Button
            onClick={() => setVoirUnJour((v) => !v)}
            color="inherit"
            startIcon={voirUnJour ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            sx={{ px: 0 }}
          >
            Un jour ({vue.sansDate.length})
          </Button>
          <Collapse in={voirUnJour}>
            <List disablePadding aria-label="Un jour">
              {vue.sansDate.map(ligne)}
            </List>
          </Collapse>
        </Box>
      )}

      {vue.faitesAujourdhui.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Button
            onClick={() => setVoirFaites((v) => !v)}
            color="inherit"
            startIcon={voirFaites ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            sx={{ px: 0 }}
          >
            Fait aujourd’hui ({vue.faitesAujourdhui.length})
          </Button>
          <Collapse in={voirFaites}>
            <List disablePadding aria-label="Fait aujourd’hui">
              {vue.faitesAujourdhui.map(ligne)}
            </List>
          </Collapse>
        </Box>
      )}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          position: 'fixed',
          bottom: `calc(${tailles.navigation + 16}px + env(safe-area-inset-bottom))`,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        {fonctionActive(session, 'vide_ma_tete') && (
          <Fab
            color="primary"
            size="medium"
            aria-label="Je vide ma tête"
            onClick={() => setCapture('vide_ma_tete')}
          >
            <Wind size={22} />
          </Fab>
        )}
        <Fab
          color="secondary"
          variant="extended"
          aria-label="Dire"
          onClick={() => setCapture('normal')}
          sx={{ px: 3, height: 56 }}
        >
          <Mic size={24} style={{ marginRight: 8 }} />
          Dire
        </Fab>
        <Fab color="primary" size="medium" aria-label="Noter à la main" onClick={ouvrirCreation}>
          <Pencil size={20} />
        </Fab>
      </Stack>

      <FeuilleCapture
        ouvert={capture !== null}
        mode={capture ?? 'normal'}
        onFermer={() => setCapture(null)}
        onCapture={chargerBoite}
      />

      {feuilles}
    </Box>
  );
}
